import { useCallback, useEffect, useRef, useState } from 'react';
import { createCallExport, defaultExportRange, describeExport, EXPORT_CONTENT_LABELS, EXPORT_KIND_LABELS, EXPORT_STATUS, fetchCallExportDownload, listCallExports, type CallExportDownload, type CallExportSummary, type ExportContent, type ExportKind } from '../lib/callExportsClient';

export type CallExportPanelProps = {
  apiBaseUrl: string;
  getAuthHeaders: () => Record<string, string>;
  onClose: () => void;
  /** intervalo entre consultas do estado (ms) */
  pollIntervalMs?: number;
};

const RECENT_LIMIT = 3;

/**
 * Exportação em lote do histórico do próprio usuário (áudios em zip ou dados em
 * planilha, por período), pela mesma exportação do Callix web. O Callix prepara o
 * arquivo em minutos; o painel acompanha e mostra o link quando fica pronto.
 */
export function CallExportPanel({ apiBaseUrl, getAuthHeaders, onClose, pollIntervalMs = 5000 }: CallExportPanelProps) {
  const [range] = useState(() => defaultExportRange());
  const [kind, setKind] = useState<ExportKind>('manual');
  const [content, setContent] = useState<ExportContent>('audio');
  const [from, setFrom] = useState(range.from);
  const [to, setTo] = useState(range.to);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [job, setJob] = useState<CallExportSummary | null>(null);
  const [download, setDownload] = useState<CallExportDownload | null>(null);
  const [recent, setRecent] = useState<CallExportSummary[]>([]);
  const [opening, setOpening] = useState<string | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // exportações recentes deste usuário (podem ter sido pedidas em outra sessão)
  useEffect(() => {
    let active = true;
    listCallExports(apiBaseUrl, getAuthHeaders())
      .then((items) => {
        if (active) setRecent(items.slice(0, RECENT_LIMIT));
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [apiBaseUrl, getAuthHeaders]);

  // acompanha a exportação pedida até ficar pronta (ou falhar)
  useEffect(() => {
    if (!job || download?.state === 'ready' || download?.state === 'failed') return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let active = true;
    const check = async () => {
      const result = await fetchCallExportDownload(apiBaseUrl, getAuthHeaders(), job.id);
      if (!active) return;
      setDownload(result);
      if (result.state === 'pending') {
        setJob(result.export);
        timer = setTimeout(check, pollIntervalMs);
      } else if (result.state === 'ready') {
        setJob(result.export);
        setRecent((items) => [result.export, ...items.filter((i) => i.id !== result.export.id)].slice(0, RECENT_LIMIT));
      }
    };
    void check();
    return () => {
      active = false;
      if (timer) clearTimeout(timer);
    };
    // o estado `download` muda a cada consulta; o laço é governado por `job`
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id, apiBaseUrl, getAuthHeaders, pollIntervalMs]);

  const submit = useCallback(async () => {
    setError(null);
    if (!from || !to) {
      setError('Informe o período.');
      return;
    }
    if (to < from) {
      setError('O fim do período vem antes do início.');
      return;
    }
    setSubmitting(true);
    setDownload(null);
    try {
      const created = await createCallExport(apiBaseUrl, getAuthHeaders(), { kind, content, from, to });
      if (!mounted.current) return;
      setJob(created);
      setRecent((items) => [created, ...items.filter((i) => i.id !== created.id)].slice(0, RECENT_LIMIT));
    } catch (err) {
      if (mounted.current) setError(err instanceof Error ? err.message : 'Não foi possível pedir a exportação.');
    } finally {
      if (mounted.current) setSubmitting(false);
    }
  }, [apiBaseUrl, getAuthHeaders, kind, content, from, to]);

  const openRecent = useCallback(
    async (item: CallExportSummary) => {
      setOpening(item.id);
      setError(null);
      try {
        const result = await fetchCallExportDownload(apiBaseUrl, getAuthHeaders(), item.id);
        if (!mounted.current) return;
        if (result.state === 'ready') window.open(result.url, '_blank', 'noopener,noreferrer');
        else setError(result.state === 'pending' ? describeExport(result.export) : result.message);
      } finally {
        if (mounted.current) setOpening(null);
      }
    },
    [apiBaseUrl, getAuthHeaders]
  );

  return (
    <div className="export-panel" role="region" aria-label="Exportar histórico">
      <div className="export-panel-header">
        <span className="export-panel-title">Exportar histórico</span>
        <button type="button" className="export-panel-close" onClick={onClose} aria-label="Fechar exportação">
          <i className="fas fa-xmark" aria-hidden="true" />
        </button>
      </div>
      <div className="export-panel-form">
        <label className="export-panel-field">
          <span>Tipo</span>
          <select value={kind} onChange={(event) => setKind(event.target.value as ExportKind)} aria-label="Tipo de chamada">
            {(Object.keys(EXPORT_KIND_LABELS) as ExportKind[]).map((value) => (
              <option key={value} value={value}>
                {EXPORT_KIND_LABELS[value]}
              </option>
            ))}
          </select>
        </label>
        <label className="export-panel-field">
          <span>Conteúdo</span>
          <select value={content} onChange={(event) => setContent(event.target.value as ExportContent)} aria-label="Conteúdo">
            {(Object.keys(EXPORT_CONTENT_LABELS) as ExportContent[]).map((value) => (
              <option key={value} value={value}>
                {EXPORT_CONTENT_LABELS[value]}
              </option>
            ))}
          </select>
        </label>
        <label className="export-panel-field">
          <span>De</span>
          <input type="date" value={from} max={to} onChange={(event) => setFrom(event.target.value)} aria-label="Início do período" />
        </label>
        <label className="export-panel-field">
          <span>Até</span>
          <input type="date" value={to} min={from} onChange={(event) => setTo(event.target.value)} aria-label="Fim do período" />
        </label>
        <button type="button" className="retry-btn export-panel-submit" onClick={() => void submit()} disabled={submitting}>
          {submitting ? 'Pedindo...' : 'Exportar'}
        </button>
      </div>
      {error && (
        <div className="export-panel-error" role="alert">
          {error}
        </div>
      )}
      {job && (
        <div className="export-panel-job" role="status">
          <span>{download?.state === 'failed' ? download.message : describeExport(job)}</span>
          {download?.state === 'ready' && (
            <a className="export-panel-link" href={download.url} target="_blank" rel="noopener noreferrer">
              <i className="fas fa-download" aria-hidden="true" /> Baixar {download.filename ?? 'arquivo'}
            </a>
          )}
        </div>
      )}
      {recent.length > 0 && (
        <ul className="export-panel-recent" aria-label="Exportações recentes">
          {recent.map((item) => (
            <li key={item.id}>
              <span>{describeExport(item)}</span>
              {item.status === EXPORT_STATUS.completed && (
                <button type="button" className="export-panel-recent-btn" onClick={() => void openRecent(item)} disabled={opening === item.id}>
                  {opening === item.id ? '...' : 'Baixar'}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <p className="export-panel-hint">O Callix prepara o arquivo em alguns minutos; o link de download vale por cerca de uma hora.</p>
    </div>
  );
}
